import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { Badge } from 'src/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import type { MediaScanHold } from './media-scan';

const COPY: Record<
  MediaScanHold,
  { label: string; title: string; hint: string; variant: 'neutral' | 'warning' | 'destructive' }
> = {
  scanning: {
    label: 'Scanning…',
    title: 'Checking this file for viruses',
    hint: "This file is being checked for viruses. It'll be available in a few minutes.",
    variant: 'neutral',
  },
  review: {
    label: 'Held for review',
    title: 'File held for review',
    hint: "The scan couldn't finish. The file stays locked until it's reviewed.",
    variant: 'warning',
  },
  blocked: {
    label: 'Blocked',
    title: 'File blocked',
    hint: "This file failed the security check. Don't ask the client to re-send it as-is.",
    variant: 'destructive',
  },
};

const ScanIcon: React.FC<{ hold: MediaScanHold; size: number; className?: string }> = ({
  hold,
  size,
  className,
}) => {
  if (hold === 'scanning') {
    return <Loader2 size={size} className={cn('tw-shrink-0 tw-animate-spin', className)} />;
  }
  if (hold === 'review') {
    return <ShieldQuestion size={size} className={cn('tw-shrink-0', className)} />;
  }
  return <ShieldAlert size={size} className={cn('tw-shrink-0', className)} />;
};

/** Subtle note for a file released without a finished scan; it's usable as normal. */
export const MediaNotScannedBadge: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Badge
            variant={'neutral'}
            className={cn('tw-shrink-0 tw-whitespace-nowrap tw-text-xs tw-font-medium', className)}
          >
            {t('Not scanned')}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className={'tw-max-w-xs'}>
          {t('Released without a virus scan because the scanner was unavailable.')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/** Small pill with a tooltip, for cards and file lists. */
export const MediaScanStatus: React.FC<{ hold: MediaScanHold; className?: string }> = ({
  hold,
  className,
}) => {
  const { t } = useTranslation();
  const copy = COPY[hold];

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Badge
            variant={copy.variant}
            className={cn('tw-shrink-0 tw-gap-1 tw-whitespace-nowrap tw-text-xs', className)}
          >
            <ScanIcon hold={hold} size={12} />
            {t(copy.label)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className={'tw-max-w-xs'}>{t(copy.hint)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Fills a preview area (player, image viewer) in place of the file itself.
 * `compact` is for thumbnail-sized tiles: just the icon, the text as a tooltip.
 */
export const MediaScanPlaceholder: React.FC<{
  hold: MediaScanHold;
  compact?: boolean;
  className?: string;
}> = ({ hold, compact = false, className }) => {
  const { t } = useTranslation();
  const copy = COPY[hold];
  const tone =
    hold === 'blocked' ? 'tw-bg-red-50 tw-text-red-900' : 'tw-bg-neutral-100 tw-text-neutral-700';

  if (compact) {
    return (
      <div
        role={'img'}
        aria-label={t(copy.label)!}
        title={`${t(copy.label)} ${t(copy.hint)}`}
        className={cn(
          'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-md',
          tone,
          className,
        )}
      >
        <ScanIcon hold={hold} size={20} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'tw-flex tw-h-full tw-min-h-[12rem] tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-lg tw-p-6 tw-text-center',
        tone,
        className,
      )}
    >
      <ScanIcon hold={hold} size={36} />
      <p className={'tw-text-base tw-font-semibold'}>{t(copy.title)}</p>
      <p className={'tw-max-w-md tw-text-sm'}>{t(copy.hint)}</p>
    </div>
  );
};
